import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import IdleScreenTimeModal from "../modal/IdleScreenTimeModal";
import SignOutConfirmModal from "../modal/SignOutConfirmModal";
import LogoutSuccessModal from "../modal/LogoutSuccessModal";
import { AppDispatch, RootState } from "../redux/store";
import { loadKioskTimeout } from "../redux/slices/kioskSlice";
import { loadMuseumFeeTax } from "../redux/slices/museumSlice";
import { clearFavourites } from "../redux/slices/favouritesSlice";
import { clearStoredAuthUser, getStoredAuthUser } from "../utils/authSession";
import { resetKioskLandingAppState } from "../utils/stationFlowStorage";
import {
  DEFAULT_KIOSK_TIMEOUT_MS,
  DEFAULT_LOGOUT_SESSION_MS,
} from "../services/kioskTimeoutService";

const ACTIVITY_RESCHEDULE_MS = 300;

/** Idle session modal is paused on these `/:slug/...` screens. */
const IDLE_MODAL_SKIP_SEGMENTS = new Set([
  "upload-image-qr-code",
  "image-transformation",
  "transformation-result",
  "select-art-background",
  "qr-scan-to-pay",
  "creating-person-hobby",
  "extract-person",
  "created-person-hobby",
  "thank-you",
  "order-confirmation",
  "all",
]);

const ACTIVITY_EVENTS = [
  "pointerdown",
  "mousedown",
  "keydown",
  "touchstart",
  "click",
  "scroll",
  "wheel",
] as const;

const isHomePath = (pathname: string) => pathname === "/";

const pathnameSegment = (pathname: string, index: number): string | null => {
  const parts = pathname.split("/").filter(Boolean);
  return parts[index] ?? null;
};

const isIdleModalSkipPath = (pathname: string) => {
  const segment = pathnameSegment(pathname, 1);
  if (segment && IDLE_MODAL_SKIP_SEGMENTS.has(segment)) return true;
  const first = pathnameSegment(pathname, 0);
  return Boolean(first && IDLE_MODAL_SKIP_SEGMENTS.has(first));
};

const shouldTrackIdle = (pathname: string) => {
  if (isHomePath(pathname)) return false;
  if (isIdleModalSkipPath(pathname)) return false;
  return true;
};

const KioskSessionManager = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { milliseconds, logoutSessionMs, loaded, loading } = useSelector(
    (state: RootState) => state.kiosk
  );
  const {
    loaded: museumLoaded,
    loading: museumLoading,
  } = useSelector((state: RootState) => state.museum);

  const idleWarningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionExpireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activityRescheduleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimeoutMsRef = useRef(
    milliseconds > 0 ? milliseconds : DEFAULT_KIOSK_TIMEOUT_MS
  );
  const logoutTimeoutMsRef = useRef(
    logoutSessionMs > 0 ? logoutSessionMs : DEFAULT_LOGOUT_SESSION_MS
  );
  const loadedRef = useRef(loaded);
  const pathnameRef = useRef(location.pathname);
  const isLoggedInRef = useRef(Boolean(getStoredAuthUser()?.id));
  const scheduleIdleWarningTimerRef = useRef<() => void>(() => undefined);

  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(getStoredAuthUser()?.id));
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showLogoutSuccessModal, setShowLogoutSuccessModal] = useState(false);
  const showIdleModalRef = useRef(false);
  const showLogoutModalRef = useRef(false);

  idleTimeoutMsRef.current =
    milliseconds > 0 ? milliseconds : DEFAULT_KIOSK_TIMEOUT_MS;
  logoutTimeoutMsRef.current =
    logoutSessionMs > 0 ? logoutSessionMs : DEFAULT_LOGOUT_SESSION_MS;
  loadedRef.current = loaded;
  pathnameRef.current = location.pathname;
  isLoggedInRef.current = isLoggedIn;
  showIdleModalRef.current = showIdleModal;
  showLogoutModalRef.current = showLogoutModal;

  const getActiveIdleMs = useCallback(() => {
    if (isLoggedInRef.current) {
      return logoutTimeoutMsRef.current;
    }
    return idleTimeoutMsRef.current;
  }, []);

  useEffect(() => {
    if (loaded || loading) return;
    dispatch(loadKioskTimeout());
  }, [dispatch, loaded, loading]);

  useEffect(() => {
    if (museumLoaded || museumLoading) return;
    dispatch(loadMuseumFeeTax());
  }, [dispatch, museumLoaded, museumLoading]);

  useEffect(() => {
    const syncAuthUser = () => {
      setIsLoggedIn(Boolean(getStoredAuthUser()?.id));
    };

    syncAuthUser();
    window.addEventListener("authUserUpdated", syncAuthUser);
    window.addEventListener("pageshow", syncAuthUser);

    return () => {
      window.removeEventListener("authUserUpdated", syncAuthUser);
      window.removeEventListener("pageshow", syncAuthUser);
    };
  }, []);

  const logoutUserIfNeeded = useCallback(() => {
    if (!getStoredAuthUser()) return;
    clearStoredAuthUser();
    dispatch(clearFavourites());
  }, [dispatch]);

  const performLoggedInLogout = useCallback(() => {
    setShowLogoutModal(false);
    logoutUserIfNeeded();
    setIsLoggedIn(false);
    setShowLogoutSuccessModal(true);
  }, [logoutUserIfNeeded]);

  const performLoggedInLogoutRef = useRef(performLoggedInLogout);
  performLoggedInLogoutRef.current = performLoggedInLogout;

  const goToIdle = useCallback(() => {
    setShowIdleModal(false);
    setShowLogoutModal(false);
    logoutUserIfNeeded();
    resetKioskLandingAppState(dispatch);
    navigate("/", { replace: true });
  }, [dispatch, logoutUserIfNeeded, navigate]);

  const goToIdleRef = useRef(goToIdle);
  goToIdleRef.current = goToIdle;

  const clearWarningAndExpireTimers = useCallback(() => {
    if (idleWarningTimerRef.current) {
      clearTimeout(idleWarningTimerRef.current);
      idleWarningTimerRef.current = null;
    }
    if (sessionExpireTimerRef.current) {
      clearTimeout(sessionExpireTimerRef.current);
      sessionExpireTimerRef.current = null;
    }
  }, []);

  const clearAllIdleTimers = useCallback(() => {
    clearWarningAndExpireTimers();
    if (activityRescheduleRef.current) {
      clearTimeout(activityRescheduleRef.current);
      activityRescheduleRef.current = null;
    }
  }, [clearWarningAndExpireTimers]);

  const startSessionExpireTimer = useCallback(() => {
    if (sessionExpireTimerRef.current) {
      clearTimeout(sessionExpireTimerRef.current);
    }

    const expireMs = getActiveIdleMs();
    if (expireMs <= 0) return;

    sessionExpireTimerRef.current = setTimeout(() => {
      if (isLoggedInRef.current) {
        performLoggedInLogoutRef.current();
        return;
      }
      goToIdleRef.current();
    }, expireMs);
  }, [getActiveIdleMs]);

  const showIdleWarning = useCallback(() => {
    if (isLoggedInRef.current) {
      setShowLogoutModal(true);
    } else {
      setShowIdleModal(true);
    }
    startSessionExpireTimer();
  }, [startSessionExpireTimer]);

  const scheduleIdleWarningTimer = useCallback(() => {
    if (idleWarningTimerRef.current) {
      clearTimeout(idleWarningTimerRef.current);
      idleWarningTimerRef.current = null;
    }

    if (
      !loadedRef.current ||
      !shouldTrackIdle(pathnameRef.current) ||
      showIdleModalRef.current ||
      showLogoutModalRef.current
    ) {
      return;
    }

    const warningMs = getActiveIdleMs();
    if (warningMs <= 0) return;

    idleWarningTimerRef.current = setTimeout(() => {
      showIdleWarning();
    }, warningMs);
  }, [getActiveIdleMs, showIdleWarning]);

  scheduleIdleWarningTimerRef.current = scheduleIdleWarningTimer;

  const dismissModalsAndRestartIdle = useCallback(() => {
    clearAllIdleTimers();
    setShowIdleModal(false);
    setShowLogoutModal(false);
    scheduleIdleWarningTimerRef.current();
  }, [clearAllIdleTimers]);

  const handleIdleContinue = useCallback(() => {
    dismissModalsAndRestartIdle();
  }, [dismissModalsAndRestartIdle]);

  const handleIdleQuit = useCallback(() => {
    clearAllIdleTimers();
    setShowIdleModal(false);
    setShowLogoutModal(false);
    logoutUserIfNeeded();
    resetKioskLandingAppState(dispatch);
    navigate("/", { replace: true });
  }, [clearAllIdleTimers, dispatch, logoutUserIfNeeded, navigate]);

  const handleLogoutCancel = useCallback(() => {
    dismissModalsAndRestartIdle();
  }, [dismissModalsAndRestartIdle]);

  const handleLogoutConfirm = useCallback(() => {
    clearAllIdleTimers();
    performLoggedInLogout();
  }, [clearAllIdleTimers, performLoggedInLogout]);

  // Start / restart idle tracking when route, auth, or API timeout changes.
  useEffect(() => {
    if (!loaded) return;

    if (!shouldTrackIdle(location.pathname)) {
      clearAllIdleTimers();
      setShowIdleModal(false);
      setShowLogoutModal(false);
      return;
    }

    if (showIdleModalRef.current || showLogoutModalRef.current) {
      return;
    }

    clearWarningAndExpireTimers();
    scheduleIdleWarningTimer();
  }, [
    clearAllIdleTimers,
    clearWarningAndExpireTimers,
    loaded,
    location.pathname,
    milliseconds,
    logoutSessionMs,
    isLoggedIn,
    scheduleIdleWarningTimer,
  ]);

  // Any real user interaction immediately cancels the idle countdown,
  // then restarts it after a short quiet period.
  useEffect(() => {
    if (!loaded || !shouldTrackIdle(location.pathname)) return;

    const onActivity = () => {
      // While a warning/logout modal is open, ignore activity here.
      // Modal buttons own dismiss — closing on capture causes click-through
      // onto product cards underneath.
      if (showIdleModalRef.current || showLogoutModalRef.current) {
        return;
      }

      // Stop the running idle clock immediately so it cannot fire mid-gesture
      clearWarningAndExpireTimers();

      if (activityRescheduleRef.current) {
        clearTimeout(activityRescheduleRef.current);
      }

      activityRescheduleRef.current = setTimeout(() => {
        activityRescheduleRef.current = null;
        scheduleIdleWarningTimerRef.current();
      }, ACTIVITY_RESCHEDULE_MS);
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true, capture: true })
    );

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, onActivity, true)
      );
    };
  }, [clearWarningAndExpireTimers, loaded, location.pathname]);

  useEffect(() => {
    return () => {
      clearAllIdleTimers();
    };
  }, [clearAllIdleTimers]);

  return (
    <>
      <IdleScreenTimeModal
        show={showIdleModal}
        onHide={handleIdleContinue}
        onContinue={handleIdleContinue}
        onQuit={handleIdleQuit}
      />
      {showLogoutModal && (
        <SignOutConfirmModal
          show={showLogoutModal}
          onHide={handleLogoutCancel}
          onConfirm={handleLogoutConfirm}
        />
      )}
      {showLogoutSuccessModal && (
        <LogoutSuccessModal
          show={showLogoutSuccessModal}
          onHide={() => setShowLogoutSuccessModal(false)}
        />
      )}
    </>
  );
};

export default KioskSessionManager;
